import { registerTenant, TenantConfig, TenantId } from '~/tenants/common/registry';
import { TWT_CUSTOMER_JOB_METADATA } from '~/tenants/twt/job-metadata';
import { TimeZone } from '~/lib/enum';
import { TWT_CUSTOMER_ORDER_METADATA, TwtOrder, TwtOrderContext, TwtOrderSchema } from '~/tenants/twt/model/TwtOrder';
import TwtOrderForm from '~/tenants/twt/forms/TwtOrderForm';
import TwtOrderWizardType from '~/tenants/twt/forms/TwtOrderWizardType';
import TwtPhotoConfig, { TwtPhotoType } from '~/tenants/twt/performable/photo/TwtPhotoConfig';
import TwtCustomer, { TwtCustomerSchema } from '~/tenants/twt/model/TwtCustomer';
import TwtProviderForm from '~/tenants/twt/forms/TwtProviderForm';
import TwtCustomerForm from '~/tenants/twt/forms/TwtCustomerForm';
import { TWT_PROVIDER_DATA } from '~/tenants/twt/data';
import TwtProvider from '~/tenants/twt/model/TwtProvider';
import { Holiday } from '~common/holidays/holidays';
import TwtEditingConfig from '~/tenants/twt/performable/editing/TwtEditingConfig';
import TwtOrderTimes from '~/tenants/twt/forms/TwtOrderTimes';
import TwtOnboardingForm from '~/tenants/twt/forms/TwtOnboardingForm';
import TwtPhotoReshootForm from './performable/photo/TwtPhotoReshootForm';
import TwtOrderPayment from '~/tenants/twt/forms/TwtOrderPayment';
import { DeliverableType } from '~common/model/Deliverable';
import TwtEditingAction from '~/tenants/twt/performable/editing/TwtEditingAction';
import TWT_PERFORMABLES from '~/tenants/twt/register/configs';
import TWT_PACKAGES from '~/tenants/twt/register/packages';
import TwtScheduleAppointmentPopup from '~/tenants/twt/views/TwtScheduleAppointmentPopup';

/**
 * Twist Tours' full tenant options, exported so tenant clones (twt2)
 * can reuse the classes/forms/performables/packages wholesale and
 * override only identity fields. Returns a fresh object per call so a
 * clone's spread-overrides never mutate twt's own registration.
 */
export function twtTenantOptions(): Omit<TenantConfig<TwtOrderContext>, 'id' | 'orderSchema'> {
  return {
    name: 'Twist Tours',
    domain: 'order.twisttours.com',
    emailTech: 'techsupport@twisttours.com',
    background: '#f9590f',
    phone: '+18666480022',
    tax: '0.0825',
    // The $16 vertical video is taxed here (registry.ts reelTaxable).
    reelTaxable: true,
    smsPrefix: 'Twist Tours, ',
    emailMissingMessage: 'We recently transitioned to a new ordering system and could not find an account for that email address. If you have not used the new system before, please create a new account using one of the options below.',
    rainforestEnable: true,
    disableReview: true,
    rainforestMerchantId: 'mid_2lOERJFs1f03OkQLaVT7cn6kj8q',
    rainforestMerchantApplicationId: 'app_2lOERHnj4mSI9srhmchUCyAOFM5',
    logoRaster: 'https://media.photology.co/tenant/twt/logo.png',
    logoVector: 'https://media.photology.co/tenant/twt/logo.svg',
    legal: '331 Enterprises, LLC',
    email: 'office@twisttours.com',
    emailEnable: true,
    emailOrderCompleteDisabled: true,
    inboxEnabled: true,
    nylasProviderInvite: true,
    phaseDefault: 'delivered',
    providers: TWT_PROVIDER_DATA,
    payoutStart: '2025-03-01T00:00:00.000Z',
    providerClass: TwtProvider,
    wizardDay: true,
    // Twist keeps its earlier time picker; the other tenants pick arrival
    // windows (founder, 9/28/26).
    legacyTimePicker: true,
    // Reschedule, hold and cancel are office only for now: agents contact
    // the office (founder, 9/28/26). In the shared options so the twt2 clone
    // behaves like Twist.
    customerChangesDisabled: true,
    wizardProvider: true,
    wizardServicesTitle: 'Photos Only & A La Carte Services',
    holidays: [Holiday.CHRISTMAS, Holiday.THANKSGIVING, Holiday.NEW_YEARS, Holiday.DAY_AFTER_THANKSGIVING],
    scheduleStartTime: '07:00',
    scheduleEndTime: '23:00',
    scheduleMinDaytime: '09:00',
    scheduleMaxDaytime: '15:30',
    scheduleJobColor: true,
    showCalculator: true,
    scheduleDayLock: true,
    // No "Still not locked an hour after scheduling" alerts: Twist's office
    // doesn't work that way (founder, 9/28/26). Only the default; the office
    // can switch it back on under Company settings -> Scheduling.
    alertUnlockedTime: false,
    scheduleHideCustomer: true,
    brands: {
      maverick: {
        name: 'Maverick Studio Photography',
        domain: 'maverick.twisttours.com',
        location: 'san',
        emailFrom: 'Maverick | Twist Tours',
        logoRaster: 'https://media.photology.co/tenant/twt/maverick.png',
        logoVector: 'https://media.photology.co/tenant/twt/maverick.svg',
      }
    },
    // TODO: fix typing
    scheduleHover: TwtScheduleAppointmentPopup as any,
    autorespondStart: '17:00',
    autorespondEnd: '08:30',
    autorespondWeekend: true,
    autorespondKeywords: {
      prepare:
        'To see a guide of how to prepare for your shoot, please visit: https://www.twisttours.com/how-to-prep-for-a-photo-shoot/',
    },
    autorespondHolidays: {
      [Holiday.NEW_YEARS_EVE]:
        'Thank you for reaching out! We are currently closed for New Year’s Eve, but we will get back to you as soon as we can!',
      [Holiday.NEW_YEARS]:
        'Thank you for reaching out! We are currently closed for New Year’s Day, but we will get back to you as soon as we can!',
      [Holiday.THANKSGIVING]:
        'Thank you for reaching out! We are currently closed for Thanksgiving, but we will get back to you as soon as we can!',
      [Holiday.JULY_4TH]:
        'Thank you for reaching out! We are currently closed for July 4th, but we will get back to you as soon as we can!',
      [Holiday.MEMORIAL_DAY]:
        'Thank you for reaching out! We are currently closed for Memorial Day, but we will get back to you as soon as we can!',
      [Holiday.CHRISTMAS_EVE]:
        'Thank you for reaching out and Happy Holidays! We are currently closed in observance of the holiday, but we will get back to you as soon as we can!',
      [Holiday.CHRISTMAS]:
        'Thank you for reaching out and Happy Holidays! We are currently closed in observance of the holiday, but we will get back to you as soon as we can!',
    },
    autorespondMessage:
      'Hello! Twist Tours is closed - please note we will have a delayed response outside of business hours. We are open M-F, 8:30am - 5pm.',
    locations: [
      {
        slug: 'atx',
        name: 'Austin',
        timezone: TimeZone.US_CENTRAL,
        address: {
          line1: '701 Tillery St #12-228',
          city: 'Austin',
          state: 'TX',
          zip: '78702',
          long: -97.6867845,
          lat: 30.2591101,
        },
      },
      {
        slug: 'san',
        name: 'San Antonio',
        timezone: TimeZone.US_CENTRAL,
        address: {
          line1: 'Downtown',
          city: 'San Antonio',
          state: 'TX',
          zip: '78205',
          long: -98.4891503389352,
          lat: 29.428041061444006,
        },
      },
      {
        slug: 'hou',
        name: 'Houston',
        timezone: TimeZone.US_CENTRAL,
        address: {
          line1: 'Downtown',
          city: 'Houston',
          state: 'TX',
          zip: '77002',
          long: -95.3698,
          lat: 29.7604,
        },
      },
    ],
    // TODO: make typescript happy
    orderClass: TwtOrder,
    customerClass: TwtCustomer,
    //nylasProviderInvite: true,
    userback: true,
    customerOrderMetadata: TWT_CUSTOMER_ORDER_METADATA,
    customerSchema: TwtCustomerSchema,
    customerJobMetadata: TWT_CUSTOMER_JOB_METADATA,
    forms: {
      orderConfigure: TwtOrderForm,
      wizardType: TwtOrderWizardType,
      provider: TwtProviderForm,
      customer: TwtCustomerForm,
      payment: TwtOrderPayment,
      times: TwtOrderTimes,
      onboarding: TwtOnboardingForm,
    },
    performables: TWT_PERFORMABLES,
    packages: TWT_PACKAGES,
    actions: [
      {
        id: 'edit',
        name: 'Request Edits',
        deliverable: [DeliverableType.PHOTO, DeliverableType.AERIAL],
        job: TwtEditingConfig,
        form: TwtEditingAction,
        // The photos picked and an edit for each (no Blue/Sunset Sky: TWT_CUSTOMER_JOB_METADATA).
        customer: { fields: ['images'] },
      },
      {
        id: 'reshoot',
        name: 'Request Reshoot',
        deliverable: [DeliverableType.PHOTO, DeliverableType.AERIAL],
        job: TwtPhotoConfig,
        form: TwtPhotoReshootForm,
        // TwtPhotoReshootForm: a Reshoot, its photo count and instructions; never the
        // office's count override or relicensing fee (TWT_CUSTOMER_JOB_METADATA).
        customer: { fields: ['photos', 'instructions'], fixed: { type: TwtPhotoType.RESHOOT } },
      },
    ],
  };
}

export default function registerTwt() {
  registerTenant(TenantId.TWIST_TOURS, TwtOrderSchema, {
    ...twtTenantOptions(),
    // Ask Twist Support (the office AI panel). Set here rather than in
    // twtTenantOptions() so the twt2 clone does not inherit it: the option is
    // on for twt only until someone decides otherwise.
    supportAssistant: {
      title: 'Ask Twist Support',
      monthlyCapUsd: 50,
      // Forwarded agent emails: ask@twisttours.com → (Google routing) →
      // ask-twist@photog.tech → the admin worker's email() handler.
      // 'active' since 29 Sep 2026: two real forwards from office@ arrived
      // with mx.cloudflare.net's ARC-Authentication-Results and
      // Authentication-Results on top (dkim=pass d=twisttours.com, dmarc=pass)
      // and a twisttours.com DKIM signature covering To and Cc, which is the
      // layout verify.ts checks for. Set back to 'observe' to stop replying.
      email: { inbound: 'ask-twist@photog.tech', replyTo: 'ask@twisttours.com', mode: 'active' },
    },
  });
}
